/**
 * Production-Safe Logger
 * Prevents sensitive information leakage in production
 * Automatically suppresses verbose logs in production builds
 */

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// Sensitive patterns to redact from logs
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /key/i,
  /authorization/i,
  /session/i,
  /auth/i,
];

/**
 * Redact sensitive information from log messages
 */
const redactSensitive = (message: any): any => {
  if (typeof message === 'string') {
    let redacted = message;
    SENSITIVE_PATTERNS.forEach(pattern => {
      redacted = redacted.replace(pattern, '[REDACTED]');
    });
    return redacted;
  }
  return message;
};

/**
 * Safe logger that:
 * - Shows all logs in development
 * - Only shows errors in production
 * - Redacts sensitive information
 * - Prevents information leakage
 */
export const logger = {
  /**
   * Debug logs - ONLY in development
   */
  debug: (...args: any[]) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...args.map(redactSensitive));
    }
  },

  /**
   * Info logs - ONLY in development
   */
  info: (...args: any[]) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args.map(redactSensitive));
    }
  },

  /**
   * Warning logs - Development and staging only
   */
  warn: (...args: any[]) => {
    if (isDevelopment || import.meta.env.MODE === 'staging') {
      console.warn('[WARN]', ...args.map(redactSensitive));
    }
  },

  /**
   * Error logs - Always logged (important for debugging)
   */
  error: (...args: any[]) => {
    // Always log errors but redact sensitive info
    console.error('[ERROR]', ...args.map(redactSensitive));
  },

  /**
   * Security logs - Only errors in production
   */
  security: (level: 'info' | 'warn' | 'error', ...args: any[]) => {
    if (isDevelopment) {
      console[level]('[SECURITY]', ...args.map(redactSensitive));
    } else if (level === 'error') {
      // Only log security errors in production
      console.error('[SECURITY ERROR]', ...args.map(redactSensitive));
    }
  },

  /**
   * Performance logs - ONLY in development
   */
  performance: (...args: any[]) => {
    if (isDevelopment) {
      console.info('[PERF]', ...args.map(redactSensitive));
    }
  },
};

/**
 * Suppress all console output in production (optional nuclear option)
 * Use this if you want ZERO logs in production
 */
export const suppressProductionLogs = () => {
  if (isProduction) {
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    console.warn = () => {};
    // Keep console.error for critical errors
  }
};

export default logger;
